import { useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon, type IconName } from '@/components/kbIcons';
import { asset } from '@/lib/assets';
import { useMyWorkTab, setMyWorkTab, type MyWorkTab } from '@/hooks/useMyWorkTab';
import { useAvatar } from '@/hooks/useAvatar';
import { WORKERS } from '@/data/workers';
import { COUNTRIES } from '@/data';
import { worldsOf, IMMIGRATION } from '@shared/roster';
import { PersonFace } from '@/lib/peoplePhotos';

/** Primary section ids + Resources sub-tabs (kept here for backward-compatible
 *  type imports). The nav now lives in a collapsible left Sidebar. */
export type Section = 'tools' | 'documents' | 'logbook' | 'mywork' | 'admin' | 'notifications' | 'stats' | 'planner' | 'calendar' | 'inbox' | 'actes';
export type DocsTab = 'services' | 'documents' | 'howwework' | 'trainings';

type L4 = Record<Lang, string>;
/* WHAT EACH SECTION IS, in one line — shown beside the sidebar item on hover
   (Gerónimo, 1 Oct 2026: «al hacer hover sobre la sidebar, que veas de qué se
   trata cada cosa»). */
const DESC: Record<string, L4> = {
  inbox: { en: 'Your e-mail with Kevo: AI-drafted replies, inside the portal', es: 'Tu correo con Kevo: respuestas redactadas por la IA, dentro del portal', fr: 'Votre courrier avec Kevo : réponses rédigées par l’IA, dans le portail', pt: 'O seu correio com o Kevo: respostas redigidas pela IA, no portal' },
  tickets: { en: 'Your HubSpot cases by stage — open one to work on it', es: 'Tus casos de HubSpot por etapa — abre uno para trabajarlo', fr: 'Vos dossiers HubSpot par étape — ouvrez-en un pour y travailler', pt: 'Os seus casos do HubSpot por etapa — abra um para trabalhar' },
  tasks: { en: 'What there is to do, and for whom — by date, person or status', es: 'Lo que hay que hacer y para quién — por fecha, persona o estado', fr: 'Ce qu’il y a à faire, et pour qui — par date, personne ou statut', pt: 'O que há para fazer, e para quem — por data, pessoa ou estado' },
  notes: { en: 'Notes on your cases — calls, decisions, reminders', es: 'Notas de tus casos — llamadas, decisiones, recordatorios', fr: 'Notes sur vos dossiers — appels, décisions, rappels', pt: 'Notas dos seus casos — chamadas, decisões, lembretes' },
  actes: { en: 'France: civil-record requests and dossiers', es: 'Francia: solicitudes de actas y dossiers', fr: 'France : demandes d’actes et dossiers', pt: 'França: pedidos de atos e dossiers' },
  actesPt: { en: 'Portugal: nationality folders and the documents they need', es: 'Portugal: carpetas de nacionalidad y los documentos que necesitan', fr: 'Portugal : dossiers de nationalité et leurs documents', pt: 'Portugal: pastas de nacionalidade e os documentos de que precisam' },
  calendar: { en: 'Deadlines, appointments and tasks by day', es: 'Plazos, citas y tareas por día', fr: 'Échéances, rendez-vous et tâches par jour', pt: 'Prazos, marcações e tarefas por dia' },
  handover: { en: 'Hand your cases over before a leave, and see what was handed to you', es: 'Pasa tus casos antes de una ausencia y mira lo que te pasaron', fr: 'Confiez vos dossiers avant une absence, et voyez ce qu’on vous a confié', pt: 'Passe os seus casos antes de uma ausência e veja o que lhe passaram' },
  planner: { en: 'Your week, planned', es: 'Tu semana, planificada', fr: 'Votre semaine, planifiée', pt: 'A sua semana, planeada' },
  tools: { en: 'The links and tools you use every day', es: 'Los enlaces y herramientas del día a día', fr: 'Les liens et outils de tous les jours', pt: 'As ligações e ferramentas do dia a dia' },
  documents: { en: 'Guides, templates and how we work', es: 'Guías, plantillas y cómo trabajamos', fr: 'Guides, modèles et notre façon de travailler', pt: 'Guias, modelos e como trabalhamos' },
  stats: { en: 'The team\'s figures: workload, cases and times', es: 'Las cifras del equipo: carga, casos y tiempos', fr: 'Les chiffres de l’équipe : charge, dossiers et délais', pt: 'Os números da equipa: carga, casos e tempos' },
  feedback: { en: 'Tell us what to fix or improve', es: 'Cuéntanos qué arreglar o mejorar', fr: 'Dites-nous quoi corriger ou améliorer', pt: 'Diga-nos o que corrigir ou melhorar' },
  admin: { en: 'Settings, users and portal management', es: 'Ajustes, usuarios y gestión del portal', fr: 'Réglages, utilisateurs et gestion du portail', pt: 'Definições, utilizadores e gestão do portal' },
};
const LABELS: Record<string, L4> = {
  tools: { en: 'Quick Access', es: 'Accesos', fr: 'Accès rapides', pt: 'Acessos' },
  documents: { en: 'Resources', es: 'Recursos', fr: 'Ressources', pt: 'Recursos' },
  mywork: { en: 'My work', es: 'Mi trabajo', fr: 'Mon travail', pt: 'Meu trabalho' },
  logbook: { en: 'Logbook', es: 'Bitácora', fr: 'Journal', pt: 'Diário' },
  /* "Admin panel" in every language, like Handover: it is what the section
     really is (users, roles, packages) and what people call it out loud —
     "Settings" promised personal preferences it doesn't have. */
  admin: { en: 'Admin panel', es: 'Admin panel', fr: 'Admin panel', pt: 'Admin panel' },
  /* "Statistics" read cold and bureaucratic — the section is the country's
     performance told in words: Reports. */
  stats: { en: 'Reports', es: 'Informes', fr: 'Rapports', pt: 'Relatórios' },
  planner: { en: 'Planner', es: 'Planificador', fr: 'Planificateur', pt: 'Planificador' },
  calendar: { en: 'Calendar', es: 'Calendario', fr: 'Calendrier', pt: 'Calendário' },
  inbox: { en: 'Inbox', es: 'Inbox', fr: 'Boîte de réception', pt: 'Caixa de entrada' },
  tasks: { en: 'Tasks', es: 'Tareas', fr: 'Tâches', pt: 'Tarefas' },
  tickets: { en: 'Tickets', es: 'Tickets', fr: 'Tickets', pt: 'Tickets' },
  notes: { en: 'Notes', es: 'Notas', fr: 'Notes', pt: 'Notas' },
  /* "Handover" in every language: the whole firm calls it that out loud, and a
     French sidebar reading "Transfert" was the only place in the portal where
     it had another name. */
  handover: { en: 'Handover', es: 'Handover', fr: 'Handover', pt: 'Handover' },
  feedback: { en: 'Feedback', es: 'Feedback', fr: 'Feedback', pt: 'Feedback' },
  /* France only: the civil-records world, kept under the name the team says
     out loud rather than a translated one. */
  actes: { en: 'Actes', es: 'Actes', fr: 'Actes', pt: 'Actes' },
  toolkit: { en: 'Toolkit', es: 'Herramientas', fr: 'Outils', pt: 'Ferramentas' },
  insights: { en: 'Insights', es: 'Insights', fr: 'Insights', pt: 'Insights' },
};

/** The My-work items, each with its own icon. Notes exists only for France —
 *  the caller filters by country. The order is the one asked for: Tickets first,
 *  then Tasks, Notes, Handover. */
const WORK_ITEMS: { tab: MyWorkTab; icon: IconName }[] = [
  { tab: 'tickets', icon: 'ticket' },
  { tab: 'tasks', icon: 'tasks' },
  { tab: 'notes', icon: 'note' },
  { tab: 'handover', icon: 'handover' },
];

interface Props {
  section: Section;
  onNavigate: (section: Section) => void;
  lang: Lang;
  collapsed: boolean;
  onToggle: () => void;
  /** Show the Administration entry (super admins only). */
  showAdmin?: boolean;
  /** Show the Statistics entry (heads and super admins). */
  showStats?: boolean;
  /** Show the Planner entry (Spain Recurring team + super admins). */
  showPlanner?: boolean;
  /** Kevo is not wired up yet — only super admins see the Inbox. */
  showInbox?: boolean;
  /** Pending access requests (super admins) → bell badge on Quick Access. */
  notifCount?: number;
  /** Active jurisdiction — Notes only exists for France. */
  countryCode?: string;
  /** Opens the feedback modal (any role). */
  onFeedback?: () => void;
  /** Signed-in person, for the footer identity (photo/initials + country). */
  userEmail?: string;
  /** Fallbacks when the person is not on the roster. */
  userName?: string;
  userCountry?: string;
  /** Opens the Profile modal (photo upload) — the foot identity is a button. */
  onOpenProfile?: () => void;
}

/** Collapsible left sidebar (replaces the floating "obsidian" dock).
 *
 *  "My work" is not a button any more: it is a permanent SECTION at the top,
 *  its four pages laid out one per line — Tasks, Tickets, Notes (France only),
 *  Handover — each with its own icon. Below it, Quick Access and Resources as
 *  plain items, then Feedback, and Administration pinned at the foot. */
/* ── CHACUN SA BARRE ──
   Asked for (Gerónimo, 16 Sept 2026): to be able to hide from one's own
   sidebar what one never opens. The choice is personal and lives in this
   browser, keyed by the person; nothing changes for anybody else, and
   nothing is deleted — "Customise" shows every item again, greyed, and one
   tap brings it back. Tickets and the Admin panel cannot be hidden: the
   first is the work, the second is the only way to a setting. */
type HideableId = 'inbox' | 'tasks' | 'notes' | 'actes' | 'calendar' | 'handover' | 'tools' | 'documents' | 'stats' | 'feedback';
const hideKey = (email: string) => `lexidy-sidebar-hidden:${email.toLowerCase()}`;
function readHidden(email: string): Set<HideableId> {
  try {
    const raw = localStorage.getItem(hideKey(email));
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? (arr.filter((x) => typeof x === 'string') as HideableId[]) : []);
  } catch { return new Set(); }
}
function writeHidden(email: string, set: Set<HideableId>): void {
  try { localStorage.setItem(hideKey(email), JSON.stringify([...set])); } catch { /* per-browser convenience only */ }
}

export function Sidebar({ section, onNavigate, lang, collapsed, onToggle, showAdmin, showStats, showPlanner, showInbox, notifCount = 0, countryCode, onFeedback, userEmail, userName, userCountry, onOpenProfile }: Props) {
  const [tab] = useMyWorkTab();
  const [hidden, setHidden] = useState<Set<HideableId>>(() => readHidden(userEmail || ''));
  const [editing, setEditing] = useState(false);
  const toggleHidden = (id: HideableId) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    setHidden(next);
    writeHidden(userEmail || '', next);
  };
  /* Shown, or shown greyed while customising; gone otherwise. */
  const vis = (id: HideableId) => editing || !hidden.has(id);
  const cls = (id: HideableId) => (editing && hidden.has(id) ? 'sidebar-off' : '');
  /* While customising, a tap toggles the item instead of navigating. */
  const act = (id: HideableId, go: () => void) => (editing ? toggleHidden(id) : go());
  const eye = (id: HideableId) => editing ? (
    <span className="sidebar-eye" aria-hidden><KBIcon name={hidden.has(id) ? 'eye-off' : 'eye'} size={13} /></span>
  ) : null;
  const custLabel = lang === 'es' ? 'Personalizar' : lang === 'fr' ? 'Personnaliser' : lang === 'pt' ? 'Personalizar' : 'Customise';
  const doneLabel = lang === 'es' ? 'Listo' : lang === 'fr' ? 'Terminé' : lang === 'pt' ? 'Pronto' : 'Done';
  const bell = notifCount > 0;
  const avatar = useAvatar(userEmail || '');

  const goWork = (t: MyWorkTab) => {
    setMyWorkTab(t);
    onNavigate('mywork');
  };

  const workItems = WORK_ITEMS.filter((it) => it.tab !== 'notes' || countryCode === 'FR');

  /* The foot identity mirrors the Quick Access profile card: the roster is
     the word on who this is — "geronimo.mccluskey" is a login, not a name. */
  const me = WORKERS.find((w) => w.email?.toLowerCase() === (userEmail || '').toLowerCase());
  const fullName = me?.name || userName || (userEmail ? userEmail.split('@')[0] : '');
  const firstName = fullName.split(/[.\s]+/)[0].replace(/^./, (c) => c.toUpperCase());
  const initials = fullName.split(/[.\s]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '·';
  const ownCountry = COUNTRIES.find((c) => c.code === me?.country)?.name || userCountry || '';

  /* Civil records — France's, or Portugal's for its Immigration team (and
     the admins); the server says the same thing at its own door. */
  const ptActes = !!showAdmin || !me || worldsOf(me).length === 0 || worldsOf(me).includes(IMMIGRATION);
  const actesDesc = countryCode === 'PT' ? DESC.actesPt : DESC.actes;
  /* Portugal's section is «Civil records», in every language (Gerónimo, 28 Oct 2026). */
  const actesLabel = countryCode === 'PT' ? 'Civil records' : LABELS.actes[lang];
  const actesItem = (
    <button
      className={`sidebar-item sidebar-sub ${section === 'actes' ? 'active' : ''} ${cls('actes')}`}
      onClick={() => act('actes', () => onNavigate('actes'))}
      data-tip-h={actesLabel} data-tip={actesDesc?.[lang] || ''} data-tip-side="right" aria-label={actesLabel}
    >
      <span className="sidebar-ico"><KBIcon name="layers" size={18} /></span>
      <span className="sidebar-lbl">{actesLabel}</span>
      {eye('actes')}
    </button>
  );

  return (
    <aside className="sidebar" aria-label="Primary navigation">
      <div className="sidebar-top">
        <button
          type="button"
          className="sidebar-brand bg-transparent border-0 p-0 cursor-pointer"
          onClick={() => onNavigate('tools')}
          aria-label="Home"
        >
          {/* The coral wordmark, as the brand draws it — the little mark plus
              typed-out name it replaced was our approximation. The login keeps
              its own "e" scene; in here, this IS the logo. */}
          <img className="sidebar-brand-img h-9" src={asset('images/nimo-logo-morado.png')} alt="Lexidy" />
        </button>
        <div className="sidebar-top-actions">
          {/* Morphing toggle: ☰ (collapsed) ⇆ ‹ back-arrow (expanded). The three
              bars rotate/shrink into the arrow — pure CSS transition. */}
          <button
            className="sidebar-toggle sidebar-burger"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            title={collapsed ? (lang === 'es' ? 'Abrir menú' : lang === 'fr' ? 'Ouvrir le menu' : 'Open menu') : (lang === 'es' ? 'Minimizar' : lang === 'fr' ? 'Réduire' : 'Collapse')}
          >
            <span className="sb-bar sb-bar-1" />
            <span className="sb-bar sb-bar-2" />
            <span className="sb-bar sb-bar-3" />
          </button>
        </div>
      </div>
      <nav className="sidebar-nav">
        {/* ── My work ── */}
        <span className="sidebar-cap">{LABELS.mywork[lang]}</span>
        {/* INBOX — Kevo, framed. It opens the list because it is where the
            day starts, and it is marked as a test: the link to Kevo is not
            finished, so only super admins see it at all. */}
        {showInbox && vis('inbox') && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'inbox' ? 'active' : ''} ${cls('inbox')}`}
            onClick={() => act('inbox', () => onNavigate('inbox'))}
            data-tip-h={LABELS['inbox'][lang]} data-tip={DESC['inbox']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['inbox'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="mail" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.inbox[lang]}</span>
            {eye('inbox')}
          </button>
        )}
        {/* Handover always closes the list — Tickets, Tasks, (Notes), Calendar,
            Handover — in every country. It is rendered after the calendar below. */}
        {workItems.filter((it) => it.tab !== 'handover').map((it) => {
          const active = section === 'mywork' && tab === it.tab;
          return (
            <span key={it.tab} className="sidebar-pair">
              {/* France's civil records belong to the work, not to a toolbox:
                  they sit between Tasks and Notes, where the person looking for
                  a client's birth certificate already is. */}
              {it.tab === 'notes' && countryCode === 'FR' && vis('actes') && actesItem}
              {(it.tab === 'tickets' || vis(it.tab as HideableId)) && (
                <button
                  className={`sidebar-item sidebar-sub ${active ? 'active' : ''} ${it.tab === 'tickets' ? '' : cls(it.tab as HideableId)}`}
                  onClick={() => (it.tab === 'tickets' ? goWork(it.tab) : act(it.tab as HideableId, () => goWork(it.tab)))}
                  data-tip-h={LABELS[it.tab][lang]} data-tip={DESC[it.tab]?.[lang] || ''} data-tip-side="right" aria-label={LABELS[it.tab][lang]}
                >
                  <span className="sidebar-ico"><KBIcon name={it.icon} size={18} /></span>
                  <span className="sidebar-lbl">{LABELS[it.tab][lang]}</span>
                  {it.tab !== 'tickets' && eye(it.tab as HideableId)}
                </button>
              )}
              {/* Portugal has no Notes tab: its Actes close the pair after Tasks. */}
              {it.tab === 'tasks' && countryCode === 'PT' && ptActes && vis('actes') && actesItem}
            </span>
          );
        })}
        {/* The calendar sits with the work it dates. It was a third tab inside
            Tasks, where it could only ever show tasks — the real appointments
            live on cases. It is a section now, and Tasks no longer has it. */}
        {vis('calendar') && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'calendar' ? 'active' : ''} ${cls('calendar')}`}
            onClick={() => act('calendar', () => onNavigate('calendar'))}
            data-tip-h={LABELS['calendar'][lang]} data-tip={DESC['calendar']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['calendar'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="calendar" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.calendar[lang]}</span>
            {eye('calendar')}
          </button>
        )}
        {vis('handover') && workItems.filter((it) => it.tab === 'handover').map((it) => {
          const active = section === 'mywork' && tab === it.tab;
          return (
            <button
              key={it.tab}
              className={`sidebar-item sidebar-sub ${active ? 'active' : ''} ${cls('handover')}`}
              onClick={() => act('handover', () => goWork(it.tab))}
              data-tip-h={LABELS[it.tab][lang]} data-tip={DESC[it.tab]?.[lang] || ''} data-tip-side="right" aria-label={LABELS[it.tab][lang]}
            >
              <span className="sidebar-ico"><KBIcon name={it.icon} size={18} /></span>
              <span className="sidebar-lbl">{LABELS[it.tab][lang]}</span>
              {eye('handover')}
            </button>
          );
        })}
        {/* THE PLANNER IS HIDDEN, NOT REMOVED. Asked for: park it until we
            come back to it. The section, its route and its data are all
            intact — `showPlanner` simply never reaches this button. Deleting
            it would have meant rebuilding it later. */}
        {false && showPlanner && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'planner' ? 'active' : ''}`}
            onClick={() => onNavigate('planner')}
            data-tip-h={LABELS['planner'][lang]} data-tip={DESC['planner']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['planner'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="clock" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.planner[lang]}</span>
          </button>
        )}

        {/* ── Toolkit ── */}
        {(vis('tools') || vis('documents')) && <span className="sidebar-cap">{LABELS.toolkit[lang]}</span>}
        {vis('tools') && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'tools' ? 'active' : ''} ${cls('tools')}`}
            onClick={() => act('tools', () => onNavigate('tools'))}
            data-tip-h={bell ? `${LABELS.tools[lang]} · ${notifCount} ${lang === 'es' ? 'solicitudes' : 'requests'}` : LABELS.tools[lang]} data-tip={DESC.tools[lang]} data-tip-side="right" aria-label={LABELS.tools[lang]}
          >
            <span className="sidebar-ico">
              <KBIcon name="tools" size={18} />
              {bell && <span className="sidebar-dot" aria-hidden />}
            </span>
            <span className="sidebar-lbl">{LABELS.tools[lang]}</span>
            {bell && (
              <span className="sidebar-bell" aria-label={`${notifCount} pending`}>
                <KBIcon name="bell" size={12} />
                <span className="sidebar-bell-n">{notifCount}</span>
              </span>
            )}
            {eye('tools')}
          </button>
        )}
        {vis('documents') && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'documents' ? 'active' : ''} ${cls('documents')}`}
            onClick={() => act('documents', () => onNavigate('documents'))}
            data-tip-h={LABELS['documents'][lang]} data-tip={DESC['documents']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['documents'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="resources" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.documents[lang]}</span>
            {eye('documents')}
          </button>
        )}

        {/* ── Insights ── */}
        {((showStats && vis('stats')) || (onFeedback && vis('feedback')) || showAdmin) && <span className="sidebar-cap">{LABELS.insights[lang]}</span>}
        {showStats && vis('stats') && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'stats' ? 'active' : ''} ${cls('stats')}`}
            onClick={() => act('stats', () => onNavigate('stats'))}
            data-tip-h={LABELS['stats'][lang]} data-tip={DESC['stats']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['stats'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="board" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.stats[lang]}</span>
            {eye('stats')}
          </button>
        )}
        {onFeedback && vis('feedback') && (
          <button className={`sidebar-item sidebar-sub sidebar-feedback ${cls('feedback')}`} onClick={() => act('feedback', onFeedback)} data-tip-h={LABELS['feedback'][lang]} data-tip={DESC['feedback']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['feedback'][lang]}>
            <span className="sidebar-ico"><KBIcon name="feedback" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.feedback[lang]}</span>
            {eye('feedback')}
          </button>
        )}
        {/* Settings (the Administration section) closes the group. */}
        {showAdmin && (
          <button
            className={`sidebar-item sidebar-sub ${section === 'admin' ? 'active' : ''}`}
            onClick={() => onNavigate('admin')}
            data-tip-h={LABELS['admin'][lang]} data-tip={DESC['admin']?.[lang] || ''} data-tip-side="right" aria-label={LABELS['admin'][lang]}
          >
            <span className="sidebar-ico"><KBIcon name="admin" size={18} /></span>
            <span className="sidebar-lbl">{LABELS.admin[lang]}</span>
          </button>
        )}

        {/* ── Foot: who is signed in ── */}
        <div className="sidebar-foot">
          {/* Customise lives IN the identity row, to the right of the name
              (Gerónimo, 23 Sept). On its own line above it, it pushed the foot
              down — on a shorter screen, or with the longer list super admins
              see, it fell out of view altogether. Only in the expanded bar:
              collapsed there is nothing to read. */}
          <div className="sidebar-foot-row">
            {fullName && (
              <button
                className="sidebar-user"
                onClick={onOpenProfile}
                title={`${fullName}${ownCountry ? ` · ${ownCountry}` : ''}`}
              >
                <span className="sidebar-photo">
                  {avatar ? <img src={avatar} alt="" /> : <span className="sidebar-ava"><PersonFace who={{ email: userEmail }}>{initials}</PersonFace></span>}
                </span>
                <span className="sidebar-user-txt">
                  <span className="sidebar-user-name">{firstName}</span>
                  {ownCountry && <span className="sidebar-user-cc">{ownCountry}</span>}
                </span>
              </button>
            )}
            {!collapsed && (
              <button type="button" className={`sidebar-cust ${editing ? 'on' : ''}`} onClick={() => setEditing((v) => !v)}
                title={editing ? doneLabel : custLabel} aria-label={editing ? doneLabel : custLabel} aria-pressed={editing}>
                <KBIcon name={editing ? 'check' : 'sliders'} size={14} />
                {editing && <span>{doneLabel}</span>}
              </button>
            )}
          </div>
        </div>
      </nav>
    </aside>
  );
}
